// The words of the "Ready to publish?" panel and the field help, written by the Chat Quality session
// (docs/agent-quality-panel.i18n.json, PR #116) and used verbatim. Change the wording there, not here.
export type Bi = { en: string; es: string };

export const PANEL = {
  "checklist": [
    {
      "id": "persona",
      "kind": "auto",
      "label": {
        "en": "The persona says what the agent does, who it helps and how it sounds",
        "es": "La personalidad dice qué hace el agente, a quién ayuda y cómo habla"
      },
      "fix": {
        "en": "Add one sentence for the job, one for the audience and one for the tone.",
        "es": "Añade una frase para el trabajo, otra para el público y otra para el tono."
      }
    },
    {
      "id": "instructions",
      "kind": "auto",
      "label": {
        "en": "Response instructions set a length and say what to do when it doesn't know",
        "es": "Las instrucciones de respuesta fijan una longitud y dicen qué hacer cuando no sabe"
      },
      "fix": {
        "en": "Add, for example: \"Answer in at most 60 words. If you don't know, say so and offer our team.\"",
        "es": "Añade, por ejemplo: \"Responde en máximo 60 palabras. Si no sabes, dilo y ofrece contactar a nuestro equipo.\""
      }
    },
    {
      "id": "knowledge",
      "kind": "auto",
      "label": {
        "en": "At least one knowledge source with content",
        "es": "Al menos una fuente de conocimiento con contenido"
      },
      "fix": {
        "en": "Add a website, file or FAQ and wait until it shows as ready.",
        "es": "Añade un sitio web, archivo o pregunta frecuente y espera a que aparezca como lista."
      }
    },
    {
      "id": "common_questions",
      "kind": "manual",
      "label": {
        "en": "Your 10 most common questions get the right answer",
        "es": "Tus 10 preguntas más comunes reciben la respuesta correcta"
      },
      "fix": {
        "en": "Ask them in the preview; if one is wrong, add or fix the page or FAQ that covers it.",
        "es": "Hazlas en la vista previa; si alguna falla, añade o corrige la página o pregunta frecuente que la cubre."
      }
    },
    {
      "id": "honest_unknown",
      "kind": "manual",
      "label": {
        "en": "A question you didn't cover gets an honest \"I don't know\" and a next step",
        "es": "Una pregunta que no cubriste recibe un \"no sé\" honesto y un siguiente paso"
      },
      "fix": {
        "en": "Tell the agent in the response instructions to offer your team or contact when it doesn't know.",
        "es": "Indica en las instrucciones que, cuando no sepa, ofrezca contactar a tu equipo."
      }
    },
    {
      "id": "languages",
      "kind": "manual",
      "label": {
        "en": "A question in each of your languages is answered in that language",
        "es": "Una pregunta en cada uno de tus idiomas se responde en ese idioma"
      },
      "fix": {
        "en": "Make sure knowledge exists for each language, or say in the instructions which language to use.",
        "es": "Asegúrate de tener conocimiento en cada idioma, o indica en las instrucciones qué idioma usar."
      }
    },
    {
      "id": "exact_wording",
      "kind": "auto",
      "label": {
        "en": "Exact wording (hours, contact, prices) is set as a topic",
        "es": "Los textos exactos (horarios, contacto, precios) están definidos como tema"
      },
      "fix": {
        "en": "Create a topic with the exact answer and the different ways people ask.",
        "es": "Crea un tema con la respuesta exacta y las distintas formas de preguntar."
      }
    },
    {
      "id": "sliders",
      "kind": "auto",
      "label": {
        "en": "Strictness is 70 or more and creativity 40 or less (recommended when wrong answers are costly)",
        "es": "El rigor es 70 o más y la creatividad 40 o menos (recomendado si un error es costoso)"
      },
      "fix": {
        "en": "Move strictness up and creativity down in Behaviour settings.",
        "es": "Sube el rigor y baja la creatividad en los ajustes de comportamiento."
      }
    },
    {
      "id": "lead_capture",
      "kind": "auto",
      "label": {
        "en": "Lead capture (if on) has a start condition, few fields and a confirmation you can keep",
        "es": "La captura de contactos (si está activa) tiene cuándo empezar, pocos campos y una confirmación que puedas cumplir"
      },
      "fix": {
        "en": "Say when to start asking, keep only the fields you need and review the confirmation message.",
        "es": "Indica cuándo empezar a pedir datos, deja solo los campos necesarios y revisa el mensaje de confirmación."
      }
    },
    {
      "id": "domains",
      "kind": "auto",
      "label": {
        "en": "Allowed domains list only your own sites",
        "es": "Los dominios permitidos son solo tus propios sitios"
      },
      "fix": {
        "en": "Remove any domain you don't own from the allowed list.",
        "es": "Quita de la lista los dominios que no sean tuyos."
      }
    }
  ],
  "tryIt": {
    "common_questions": {
      "en": "[Type one of your real top questions]",
      "es": "[Escribe una de tus preguntas más frecuentes]"
    },
    "honest_unknown": {
      "en": "Do you offer something you never mentioned?",
      "es": "¿Ofrecen algo que nunca mencionaron?"
    },
    "languages": {
      "en": [
        "Can you help me choose?",
        "¿Me pueden ayudar a elegir?"
      ],
      "es": [
        "¿Me pueden ayudar a elegir?",
        "Can you help me choose?"
      ]
    }
  },
  "warnings": {
    "no_knowledge": {
      "en": "No knowledge yet: the agent can only chat in general terms.",
      "es": "Aún no hay conocimiento: el agente solo podrá conversar en términos generales."
    },
    "strict_thin": {
      "en": "High strictness with very little knowledge may make the agent say \"I don't know\" too often.",
      "es": "Un rigor alto con muy poco conocimiento puede hacer que el agente diga \"no sé\" demasiado."
    },
    "persona_long": {
      "en": "The persona is very long. Put facts in the knowledge and keep the persona for role and tone.",
      "es": "La personalidad es muy larga. Pon los datos en el conocimiento y deja la personalidad para el rol y el tono."
    },
    "topic_generic": {
      "en": "A topic trigger is a single common word and may catch questions that need a real answer.",
      "es": "El disparador de un tema es una sola palabra común y puede interceptar preguntas que necesitan una respuesta real."
    }
  },
  "help": {
    "persona": {
      "en": "Who the agent is: its job, who it helps and its tone. Keep facts for the knowledge.",
      "es": "Quién es el agente: su trabajo, a quién ayuda y su tono. Los datos van en el conocimiento."
    },
    "responseInstructions": {
      "en": "Short rules for every answer: length, format, and what to do when it doesn't know.",
      "es": "Reglas cortas para cada respuesta: longitud, formato y qué hacer cuando no sabe."
    },
    "strictness": {
      "en": "How closely the agent sticks to your material. High: answers only from it.",
      "es": "Qué tan fiel es el agente a tu material. Alto: responde solo con él."
    },
    "creativity": {
      "en": "Freedom in wording, not in facts. Low keeps answers plain.",
      "es": "Libertad al redactar, no con los datos. Bajo mantiene respuestas sencillas."
    },
    "topics": {
      "en": "Exact answers that never go to the AI. List the different ways people ask.",
      "es": "Respuestas exactas que nunca pasan por la IA. Lista las distintas formas de preguntar."
    },
    "leadCapture": {
      "en": "Say when to start, ask for the minimum, and promise only what you can keep.",
      "es": "Indica cuándo empezar, pide lo mínimo y promete solo lo que puedas cumplir."
    }
  },
  "examples": {
    "general": {
      "persona": {
        "en": "You are the virtual assistant of [company]. You help visitors find information about our products and services. You are friendly, clear and brief. You do not give legal or medical advice and you do not talk about other companies.",
        "es": "Eres el asistente virtual de [empresa]. Ayudas a los visitantes a encontrar información sobre nuestros productos y servicios. Eres amable, claro y breve. No das consejos legales ni médicos y no hablas de otras empresas."
      },
      "responseInstructions": {
        "en": "Answer in at most 60 words unless the visitor asks for more detail. Use short paragraphs. If you don't have the answer, say so and offer to connect the visitor with our team. Never guess prices, dates or specifications.",
        "es": "Responde en un máximo de 60 palabras salvo que el visitante pida más detalle. Usa párrafos cortos. Si no tienes la respuesta, dilo y ofrece poner al visitante en contacto con nuestro equipo. Nunca adivines precios, fechas ni especificaciones."
      }
    },
    "service": {
      "persona": {
        "en": "You are the assistant of [company], a service business. You help visitors understand our services, how we work and what to prepare, and you help them request a quote. You are professional, warm and to the point. You never diagnose problems or promise results; a specialist does that.",
        "es": "Eres el asistente de [empresa], una empresa de servicios. Ayudas a los visitantes a entender nuestros servicios, cómo trabajamos y qué preparar, y a pedir un presupuesto. Eres profesional, cercano y directo. Nunca diagnosticas problemas ni prometes resultados; eso lo hace un especialista."
      },
      "responseInstructions": {
        "en": "Answer in at most 80 words. If the visitor wants a quote or to be contacted, ask for their details one question at a time. If you don't know something, say so and offer a specialist. Never quote prices that are not in the knowledge.",
        "es": "Responde en un máximo de 80 palabras. Si el visitante quiere un presupuesto o que lo contacten, pide sus datos de una pregunta a la vez. Si no sabes algo, dilo y ofrece un especialista. Nunca des precios que no estén en el conocimiento."
      }
    }
  }
} as const;

export type ChecklistId = (typeof PANEL.checklist)[number]["id"];
export type WarningId = keyof typeof PANEL.warnings;
